import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { FailureView } from '../components/FailureView.js';
import { PageShell } from '../components/PageShell.js';
import { SiteHeader } from '../components/SiteHeader.js';
import { ListSkeleton, Skeleton } from '../components/Skeleton.js';
import { api } from '../lib/api.js';
import { type Failure, explain } from '../lib/refusal.js';
import { type SitePayload, SitePayloadSchema } from '../lib/site.js';
import { FALLBACK_THEME, readTheme } from '../lib/theme.js';

// The layout route behind /e/:slug — it owns the one request the three event
// pages share.
//
// One fetch for the whole section, not one per tab: the payload is a single
// document by design (T9 composes it from four workflows), and re-requesting it
// when someone taps between Overview and Schedule would triple the load on an
// unauthenticated, rate-limited endpoint for no new information.

const SiteContext = createContext<SitePayload | null>(null);

/**
 * Supplies the event payload to everything below `/e/:slug`.
 *
 * Split out from the layout rather than inlined so a page can be rendered
 * against a payload directly. The pages read their data from context, and a
 * test about what one of them RENDERS should not have to stand up a fake
 * network to get there — that is the layout's job, and the layout has its own
 * tests.
 */
export function SiteProvider({ payload, children }: { payload: SitePayload; children: ReactNode }) {
  return <SiteContext.Provider value={payload}>{children}</SiteContext.Provider>;
}

/** The event payload for the route below `/e/:slug`. Throws outside it. */
export const useSite = (): SitePayload => {
  const payload = useContext(SiteContext);
  if (!payload) throw new Error('useSite() used outside the /e/:slug layout');
  return payload;
};

type Phase =
  | { k: 'loading' }
  | { k: 'failed'; failure: Failure }
  | { k: 'ready'; payload: SitePayload };

export function EventLayout() {
  const { slug = '' } = useParams<{ slug: string }>();
  const [phase, setPhase] = useState<Phase>({ k: 'loading' });

  // The request in flight, so a superseded one cannot land.
  //
  // Two events open in one session — a slug typed over another, a back button —
  // means two requests racing, and the loser is whichever the network happens
  // to finish last. Without this, a slow response for the *previous* event
  // would overwrite the current one and the page would quietly show the wrong
  // conference. Aborting also stops the state update that would otherwise land
  // after unmount.
  const inFlight = useRef<AbortController | null>(null);

  const load = useCallback(async (idOrSlug: string) => {
    inFlight.current?.abort();
    const attempt = new AbortController();
    inFlight.current = attempt;
    setPhase({ k: 'loading' });
    try {
      const res = await api.get<{ data: unknown }>(
        `/v1/public/events/${encodeURIComponent(idOrSlug)}/site`,
        { signal: attempt.signal },
      );
      if (attempt.signal.aborted) return;
      setPhase({ k: 'ready', payload: SitePayloadSchema.parse(res.data) });
    } catch (error) {
      // Checked before the error is read, not after: an aborted fetch rejects
      // with an AbortError, which the api client turns into a status-0
      // ApiError — indistinguishable from a dropped connection. Reading it
      // would render "you appear to be offline" on a page that is fine.
      if (attempt.signal.aborted) return;
      setPhase({ k: 'failed', failure: explain(error, 'site') });
    }
  }, []);

  useEffect(() => {
    void load(slug);
    return () => inFlight.current?.abort();
  }, [slug, load]);

  if (phase.k === 'loading') {
    return (
      <PageShell theme={FALLBACK_THEME}>
        <div aria-busy="true">
          <Skeleton className="h-8 w-1/2" />
          <Skeleton className="mt-3 h-4 w-1/3" />
          <div className="mt-8">
            <ListSkeleton rows={3} />
          </div>
        </div>
      </PageShell>
    );
  }

  if (phase.k === 'failed') {
    // The uniform 404 covers draft, archived and nonexistent alike (T9), so
    // there is nothing here to tell an attendee beyond "we could not find it" —
    // and nothing this page could infer without undoing that uniformity.
    return (
      <PageShell theme={FALLBACK_THEME}>
        <FailureView failure={phase.failure} onRetry={() => void load(slug)} />
      </PageShell>
    );
  }

  const { payload } = phase;
  return (
    <SiteProvider payload={payload}>
      <PageShell theme={readTheme(payload.theme)} header={<SiteHeader payload={payload} />}>
        <Outlet />
      </PageShell>
    </SiteProvider>
  );
}
